/**
 * Shared route map component — renders a Leaflet/OSM map with numbered pins,
 * a route polyline, time estimates, expand/collapse, and a Google Maps link.
 *
 * Used by:  Drone Ops → Today's Route
 *           Dispatch Board → Today tab
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Deal } from "../../lib/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const L: any;

// ---------- helpers ----------

const PIN_COLORS = ["#f46530", "#345c72", "#d4edf4", "#2a2e30", "#ff9e7a"];
function pinColor(idx: number): string {
  return PIN_COLORS[idx % PIN_COLORS.length];
}

function formatAddress(d: Deal): string {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ]
    .filter(Boolean)
    .join(" ");
}

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function driveMinutes(km: number): number {
  return Math.round((km / 45) * 60);
}

function googleMapsRouteLink(points: { lat: number; lon: number }[]): string {
  const pts = points.map((p) => `${p.lat},${p.lon}`);
  if (pts.length === 0) return "#";
  if (pts.length === 1)
    return `https://www.google.com/maps/dir/?api=1&destination=${pts[0]}&travelmode=driving`;
  if (pts.length === 2)
    return `https://www.google.com/maps/dir/${pts[0]}/${pts[1]}`;
  const origin = pts[0];
  const dest = pts[pts.length - 1];
  const waypoints = pts.slice(1, -1).join("/");
  return `https://www.google.com/maps/dir/${origin}/${waypoints}/${dest}`;
}

// ---------- types ----------

export interface RouteStop {
  /** Used for marker label. */
  label: string;
  /** Popup name. */
  name: string;
  /** Popup address. */
  address: string;
  lat: number;
  lon: number;
}

interface Props {
  /** Pre-ordered list of stops (map draws in this order). */
  stops: RouteStop[];
  /** Minutes spent per stop (default 7 for drone, 0 to hide fly estimate). */
  minutesPerStop?: number;
  /** Label for per-stop time in the summary line. Default "flying". */
  perStopLabel?: string;
  /** Collapsed map height class. Default "h-48". */
  collapsedHeight?: string;
  /** Expanded map height class. Default "h-96". */
  expandedHeight?: string;
}

// ---------- component ----------

export default function RouteMap({
  stops,
  minutesPerStop = 7,
  perStopLabel = "flying",
  collapsedHeight = "h-48",
  expandedHeight = "h-96",
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstance = useRef<any>(null);
  const [expanded, setExpanded] = useState(false);

  // Drive time estimate
  const totalDriveMin = useMemo(() => {
    let total = 0;
    for (let i = 1; i < stops.length; i++) {
      const prev = stops[i - 1];
      const curr = stops[i];
      total += driveMinutes(haversine(prev.lat, prev.lon, curr.lat, curr.lon));
    }
    return total;
  }, [stops]);

  const totalPerStop = stops.length * minutesPerStop;
  const totalMin = totalPerStop + totalDriveMin;
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;

  // Build / rebuild map
  useEffect(() => {
    if (!mapRef.current || typeof L === "undefined" || stops.length === 0) return;

    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }

    const map = L.map(mapRef.current, { zoomControl: true });
    mapInstance.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);

    const bounds = L.latLngBounds([]);

    stops.forEach((s, i) => {
      const color = pinColor(i);
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:30px; height:30px; border-radius:50%;
          background:${color}; color:#fff; font-weight:700;
          display:flex; align-items:center; justify-content:center;
          font-size:13px; border:2px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,.3);
        ">${s.label}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      L.marker([s.lat, s.lon], { icon })
        .addTo(map)
        .bindPopup(`<b>#${s.label} ${s.name}</b><br/>${s.address}`);
      bounds.extend([s.lat, s.lon]);
    });

    // Route polyline
    L.polyline(
      stops.map((s) => [s.lat, s.lon] as [number, number]),
      { color: "#f46530", weight: 3, opacity: 0.7, dashArray: "8 6" },
    ).addTo(map);

    map.fitBounds(bounds.pad(0.15));

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, [stops, expanded]);

  if (stops.length === 0) return null;

  return (
    <div className="mb-4 relative z-0">
      <div
        className={
          "relative rounded-xl overflow-hidden shadow border border-slate-200 transition-all " +
          (expanded ? expandedHeight : collapsedHeight)
        }
      >
        <div ref={mapRef} className="absolute inset-0" />
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="text-slate-500">
          {hrs > 0 ? `${hrs}h ` : ""}{mins}m estimated
          {minutesPerStop > 0 && ` · ${totalPerStop}m ${perStopLabel}`}
          {totalDriveMin > 0 && ` + ${totalDriveMin}m driving`}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="px-3 py-1 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200"
          >
            {expanded ? "Collapse map" : "Expand map"}
          </button>
          <a
            href={googleMapsRouteLink(stops)}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1 rounded bg-bardin-teal text-white text-xs hover:opacity-90"
          >
            Open full route in Google Maps
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------- convenience builders ----------

/** Convert an array of Deals into RouteStops (filters out deals with no coords). */
export function dealsToRouteStops(deals: Deal[]): RouteStop[] {
  return deals
    .filter((d) => d.property_lat != null && d.property_lon != null)
    .map((d, i) => ({
      label: String(i + 1),
      name: d.homeowner_name ?? "(no name)",
      address: formatAddress(d),
      lat: d.property_lat!,
      lon: d.property_lon!,
    }));
}
